import { Formik } from 'formik';
import { useParams } from 'react-router-dom';
import { Fragment } from 'react/jsx-runtime';

import { FieldCard, FormField, Skeleton } from '@components';
import { appConfigurationFormSchema, getAppConfigurationFormConfig, SCREEN_BREAKPOINTS } from '@constants';
import { FormType, InputType } from '@enums';
import { useAppDispatch, useErrorFormInner, useFormError, useFormSubmissionStatus, useMediaQuery } from '@hooks';
import { getCdnSummary, submitFormRequest, useCdnSummary, useCdnSummaryLoading } from '@store';
import type { AppConfigurationFormProps, FormFieldProps } from '@types';
import { filterSelectedValues, validateValueEqual } from '@utils';

export const AppConfigurationForm = ({ onFormikInstance }) => {
  const dispatch = useAppDispatch();
  const { id: application_id } = useParams();

  const formType = FormType.CdnApplicationConfiguration;

  const isAboveSm = useMediaQuery(`(min-width: ${SCREEN_BREAKPOINTS.sm}px)`);

  const isSubmitting = useFormSubmissionStatus({ formType });
  const submitError = useFormError({ formType });
  const isOptionLoading = useCdnSummaryLoading();
  const formLoading = useFormSubmissionStatus({ formType });

  const {
    configuration,
    name,
    http_port,
    https_port,
    max_body_size,
    max_post_argument,
    max_url_argument,
    enable_websocket,
  } = useCdnSummary();

  const {
    http_port: httpPortOption,
    https_port: httpsPortOption,
    max_body_size: maxBodySizeMaxValue,
    max_post_argument: maxPostArgumentMaxValue,
    max_url_argument: maxUrlArgumentMaxValue,
    default_http_port,
    default_https_port,
    default_url_argument,
    default_post_argument,
    default_body_size,
  } = configuration ?? [];

  const initialAppConfigurationValues = {
    http_port: http_port ? filterSelectedValues(httpPortOption, http_port) : default_http_port,
    https_port: https_port ? filterSelectedValues(httpsPortOption, https_port) : default_https_port,
    max_body_size: max_body_size ?? default_body_size,
    max_post_argument: max_post_argument ?? default_post_argument,
    max_url_argument: max_url_argument ?? default_url_argument,
    max_body_size_unit: 'mb',
    enable_websocket: enable_websocket ?? true,
    name: name ?? '',
  };

  const handleSubmit = (values: AppConfigurationFormProps) => {
    const { max_body_size_unit, ...updatedValues } = values;
    dispatch(
      submitFormRequest({
        formType,
        formData: { ...updatedValues },
        params: { content_id: application_id },
        globalOptions: {
          returnResult: true,
          content: true,
        },
        callbackFunction: { refetch: getCdnSummary(application_id) },
      }),
    );
  };

  const AppConfigurationErrorInner = () => {
    useErrorFormInner({ submitError, isSubmitting });
    return null;
  };

  const renderFormField = (field: FormFieldProps, formikValues: AppConfigurationFormProps) => {
    const isSwitch = validateValueEqual(field.inputType, InputType.Switch);

    const { name, label, description, showChip, selectedInput, ...props } = field;

    return (
      <FieldCard
        key={name}
        title={label}
        description={description}
        besideTitle={isSwitch && !isAboveSm}
        showChip={showChip}
        selectedInput={selectedInput}
      >
        <FormField name={name} label={label} key={name} {...props} />
      </FieldCard>
    );
  };

  if (isOptionLoading) {
    return (
      <div className="space-y-8">
        {[...Array(6)].map((_, index) => (
          <Skeleton key={index} className="flex w-full rounded-2xl h-32" />
        ))}
      </div>
    );
  }

  return (
    <Formik<AppConfigurationFormProps>
      initialValues={initialAppConfigurationValues}
      validationSchema={appConfigurationFormSchema}
      initialTouched={{ max_body_size: true, max_post_argument: true, max_url_argument: true }}
      onSubmit={handleSubmit}
    >
      {(formik) => {
        if (onFormikInstance) onFormikInstance(formik);
        const appConfigurationFields = getAppConfigurationFormConfig({
          schema: appConfigurationFormSchema,
          loading: formLoading,
          httpPortOption,
          httpsPortOption,
          httpSelectedInput: formik?.values?.http_port,
          httpsSelectedInput: formik?.values?.https_port,
          maxBodySizeMaxValue,
          maxPostArgumentMaxValue,
          maxUrlArgumentMaxValue,
        })?.inputList;

        return (
          <Fragment>
            <AppConfigurationErrorInner />
            {appConfigurationFields.map((field, index) => renderFormField(field, formik.values))}
          </Fragment>
        );
      }}
    </Formik>
  );
};
